import {
  Box,
  Flex,
  useToast
} from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useHistory } from "react-router-dom";
import { Column } from "react-table";
import {
  format,
  formatDuration
} from "date-fns";
import { Select } from "chakra-react-select";
import _ from "lodash";

import { useConversationsApi } from "src/api/conversations/conversations.api";
import { Header } from "src/components/shared/Header";
import { Table } from "src/components/table";
import { Text } from "src/components/text";
import { Conversation } from "src/api";
import { Avatar } from "src/components/avatar";
import { useAuthContext } from "src/hooks/useAuthContext";
import { useUsersApi } from "src/api/users/users.api";
import { ITableInstance } from "src/components/table/Table";

interface ConversationsHistoryTableData {
  _id: Conversation["_id"]
  date: Conversation["date"]
  startTime: Conversation["startTime"]
  endTime: Conversation["endTime"]
  latencySeconds: Conversation["latencySeconds"]
  topics?: Conversation["topics"];
  mode: Conversation["mode"];
  creator?: string
  partner?: string
}

export const ConversationHistoryPage: React.FC = () => {
  const history = useHistory();
  const { authDataState } = useAuthContext();
  const [ conversationsRes, conversationsReq ] = useConversationsApi("GET_CONVERSATIONS");
  const [ conversationsTableData, setConversationsTableData ] = useState<ConversationsHistoryTableData[]>([]);
  const [ listUsersRes, listUsersReq ] = useUsersApi("GET_PARTNERS");
  const [ searchedUsers, setSearchedUsers ] = useState<Array<{value: string; label: string}>>([]);
  const [ selectedUserFilter, setSelectedUserFilter ] = React.useState<string>();
  const TableRef = React.useRef<ITableInstance>(null);
  const toast = useToast();

  useEffect(() => {
    listUsersReq({});
  }, [ listUsersReq ]);

  useEffect(() => {
    if (listUsersRes.data) {
      if (authDataState.user?._id) {
        const usersList = listUsersRes.data.results.filter(u => u._id !== authDataState.user?._id);

        // Initialise searched users
        setSearchedUsers(_.map(usersList, u => ({
          label: u.name,
          value: u._id
        })));
      }
    }

    if (listUsersRes.error) {
      toast({
        status: "error",
        title: "Could not fetch users",
        description: "Please try again"
      });
    }
  }, [
    listUsersRes,
    toast,
    authDataState
  ]);

  const columns = useMemo(
    (): Array<Column<ConversationsHistoryTableData>> => [
      {
        Header: "Mode",
        accessor: "mode",
        Cell: props => (
          <div>
            {props.value === "voice" ? "Voice" : "Message"}
          </div>
        )
      },
      {
        Header: "Date",
        accessor: "date",
        Cell: props => (
          <div>
            {format(new Date(props.value), "yyyy/MM/dd")}
          </div>
        )

      },
      {
        Header: "Start Time",
        accessor: "startTime",
        Cell: props => (
          <div>
            {format(new Date(props.value), "HH:mm")}
          </div>
        )
      },
      {
        Header: "End Time",
        accessor: "endTime",
        Cell: props => (
          <div>
            {format(new Date(props.value), "HH:mm")}
          </div>
        )
      },
      {
        Header: "Latency",
        accessor: "latencySeconds",
        Cell: props => (
          <div>
            {formatDuration({
              minutes: Math.floor(props.value / 60),
              seconds: props.value - Math.floor(props.value / 60) * 60
            })}
          </div>
        )
      },
      {
        Header: "Partner",
        accessor: "partner",
        Cell: props => (
          <Avatar name={props.value || ""} />
        )
      }
    ],
    []
  );

  React.useEffect(() => {
    TableRef.current?.reloadTable();
  }, [ selectedUserFilter ]);

  const handleFetch = useCallback((pageIndex: number, pageSize: number) => {
    const skip = pageIndex * pageSize;
    const filters: {endTime: any; partner?: string} = { endTime: { $lt: new Date() } };

    if (selectedUserFilter) {
      filters.partner = selectedUserFilter;
    }

    conversationsReq({
      skip,
      filter: filters,
      sort: { "fullConversation.startTime": -1 },
      limit: pageSize
    });
  }, [ conversationsReq, selectedUserFilter ]);

  const pageCount = useMemo(() =>
    conversationsRes.data ?
      conversationsRes.data?.total < 10 ? 1 : Math.ceil(conversationsRes.data?.total / 10) :
      1,
  [ conversationsRes ]);

  useEffect(() => {
    if (conversationsRes.data) {
      const conversationsData = conversationsRes.data.results.map(conversation => {
        return {
          ...conversation,
          partner: conversation.participants?.find(p => p.user._id !== authDataState.user?._id)?.user.name
        };
      });

      setConversationsTableData(conversationsData);
    }
  }, [ conversationsRes, authDataState ]);

  return (
    <Box p={10}>
      <Header />

      <Box paddingY={10}>
        <Text as="h1">
          History
        </Text>

        <Box paddingY={4}>
          <Text as="p">
            View your past sessions
          </Text>

        </Box>

        <Flex justifyContent="flex-end">
          <Box width={300}>
            <Select
              name="partner"
              options={[
                {
                  value: undefined,
                  label: "Please select"
                },
                ...searchedUsers
              ]}
              placeholder="Filter by partner"
              closeMenuOnSelect
              onChange={(e: {value: string; label: string}) => {
                setSelectedUserFilter(e.value);
              }}
              size="md"
            />

            {listUsersRes.complete && searchedUsers.length === 0 && (
              <Text as="small">
                Nobody else is in your mission.
              </Text>
            )}
          </Box>
        </Flex>

        <Table
          ref={TableRef}
          columns={columns}
          fetchData={handleFetch}
          loading={conversationsRes.pending}
          pageCount={pageCount}
          handleClick={conversation => history.push(`/conversation/${conversation.original._id}/topic/${conversation.original.topics?.[ 0 ]._id}`)}
          data={conversationsTableData || []}
        />

      </Box>

    </Box>
  );
};
